import { enableProdMode } from '@angular/core';
import { platformBrowserDynamic } from '@angular/platform-browser-dynamic';
import { Environment as BuiltIn } from './environments/environment';
import { Environment as BuiltInProd } from './environments/environment.prod';

/**
 * Where this copy of the app sends its requests is decided when the page loads, not when the
 * app is built.
 *
 * WHY
 * ---
 * Until 2026-08-10 the backend address was fixed at build time, which meant every environment
 * needed its own build and a build could only ever be right for one place. On 2026-08-10 the
 * machine built to replace the live server was serving the LIVE build, so the review copy's
 * front end was addressing the live platform's API. Nothing reached live — the browser refused
 * the request — but no setting on the machine could have revealed or prevented it, because the
 * address was inside the compiled app.
 *
 * The FindHelp entry is overridable for the same reason: on 2026-08-19 the review site's
 * compiled-in FindHelp address still pointed at the development test box, so the client had
 * no way to reach the provider-import screen from the site she was reviewing.
 *
 * WHAT THIS DOES
 * --------------
 * Before the app starts, it reads `assets/app-config.json` from wherever it is being served.
 * Anything named there wins over what was compiled in. If the file is absent or unreadable the
 * app behaves exactly as it did before, so an environment that does not use it is unaffected.
 *
 * The app is imported only after that, deliberately. The backend address is read into a
 * constant the moment the app's own files are loaded, so loading them first would freeze the
 * compiled-in value before this had a chance to replace it.
 *
 * A settings file looks like:
 *   {
 *     "server":  "https://uat-api.mtnbugs.link/",
 *     "hostMap": { "uat.mtnbugs.link": "roanoke" },
 *     "isTestEnv": true,
 *     "findhelp": { "host": "https://uat.mtnbugs.link", "enabledCauseGroupIds": [1] }
 *   }
 * `server` must end in a slash: the app appends `api/` to it.
 */
interface RuntimeConfig {
  server?: string;
  hostMap?: { [host: string]: string };
  isTestEnv?: boolean;
  findhelp?: { host: string; enabledCauseGroupIds: number[] };
}

async function applyRuntimeConfig(): Promise<void> {
  let config: RuntimeConfig;
  try {
    const response = await fetch('assets/app-config.json', { cache: 'no-store' });
    if (!response.ok) {
      return;
    }
    config = await response.json();
  } catch {
    // No settings file, or it is not readable. The compiled-in values stand.
    return;
  }

  // Published on `window` so a person (or a proof script) can see from the browser console
  // which runtime settings the page actually loaded — the compiled objects are module-scoped
  // and invisible from outside.
  (window as any).__L2H_RUNTIME_CONFIG__ = config;

  // Both are updated because they are two different objects: the app reads its backend
  // address from one and its cause-group mapping from the other, and which is which depends
  // on how the build was configured.
  for (const target of [BuiltIn, BuiltInProd] as any[]) {
    if (typeof config.server === 'string' && config.server) {
      target.server = config.server;
    }
    if (config.hostMap) {
      target.hostMap = { ...target.hostMap, ...config.hostMap };
    }
    if (typeof config.isTestEnv === 'boolean') {
      target.isTestEnv = config.isTestEnv;
    }
    if (config.findhelp && typeof config.findhelp.host === 'string') {
      target.findhelp = { ...(target as any).findhelp, ...config.findhelp };
    }
  }
}

applyRuntimeConfig().then(async () => {
  const { AppModule } = await import('./app/app.module');

  if (BuiltIn.production) {
    enableProdMode();
  }

  const googleMapsScript = document.createElement('script');
  googleMapsScript.async = true;
  googleMapsScript.defer = true;
  googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=${(BuiltIn as any).googleMapsApiKey}`;
  document.head.appendChild(googleMapsScript);

  platformBrowserDynamic().bootstrapModule(AppModule)
    .catch((err) => console.log(err));
});
